import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Monitor, Apple, Check, Zap, Loader2, ArrowLeft, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DownloadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [email, setEmail] = useState('');
  const [verified, setVerified] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    setPollingPayment(true);

    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setPaymentSuccess(true);
          setPollingPayment(false);
          return;
        }
        if (res.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setPollingPayment(false);
          return;
        }
      } catch {
        // ignore
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPollingPayment(false);
        toast.error('Payment status check timed out. Please verify your email below.');
      }
    };

    poll();
  }, [sessionId]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/download/verify`, { email });
      setVerified(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Verification failed';
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Nav */}
      <nav className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png" 
              alt="DentalPulse" 
              className="h-10 w-auto object-contain"
              data-testid="download-nav-logo"
            />
          </button>
          <Button
            onClick={() => navigate('/login')}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Sign In
          </Button>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-16">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        {/* Payment success message */}
        {paymentSuccess && (
          <div data-testid="payment-success" className="mb-8 p-6 rounded-xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-center gap-3 mb-2">
              <Check className="w-6 h-6 text-green-400" />
              <h3 className="text-lg font-bold text-green-400">Payment Successful!</h3>
            </div>
            <p className="text-sm text-slate-300">
              Your 7-day free trial has started. Enter the email you used during checkout below to access your downloads and login credentials.
            </p>
          </div>
        )}

        {/* Polling payment */}
        {pollingPayment && (
          <div className="mb-8 p-6 rounded-xl bg-dp-500/10 border border-dp-500/30 flex items-center gap-4">
            <Loader2 className="w-6 h-6 text-dp-300 animate-spin" />
            <div>
              <p className="font-semibold text-white">Processing your payment...</p>
              <p className="text-sm text-slate-400">This usually takes just a moment.</p>
            </div>
          </div>
        )}

        <h1 className="text-3xl font-bold mb-2">Download DentalPulse</h1>
        <p className="text-slate-400 mb-8">
          Enter your email to verify your subscription and access the desktop app.
        </p>

        {/* Verify form */}
        {!verified && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Email Address</label>
              <input
                data-testid="download-email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-dp-500 focus:outline-none text-base"
                placeholder="dr.smith@practice.com"
              />
            </div>
            <Button
              data-testid="download-verify-btn"
              type="submit"
              disabled={loading}
              className="w-full py-5 bg-gradient-to-r from-dp-500 to-dp-600 hover:from-dp-400 hover:to-dp-500 text-white font-semibold rounded-xl text-base"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Download'}
            </Button>
          </form>
        )}

        {/* Download section */}
        {verified && (
          <div data-testid="download-section" className="space-y-6">
            <div className="p-5 rounded-xl bg-green-500/10 border border-green-500/30 flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-400" />
              <div>
                <p className="font-semibold text-white">{verified.practice_name}</p>
                <p className="text-sm text-slate-400">
                  {verified.is_trial
                    ? `Trial — ${verified.trial_days_remaining} days remaining`
                    : `${verified.tier.charAt(0).toUpperCase() + verified.tier.slice(1)} Plan — Active`}
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Download v{verified.version}</h3>
              <div className="space-y-3">
                <a
                  data-testid="download-windows"
                  href={verified.downloads.windows}
                  className="flex items-center gap-4 p-5 rounded-xl bg-slate-800 border border-slate-700 hover:border-dp-500/50 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Monitor className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white group-hover:text-dp-300 transition-colors">Windows</p>
                    <p className="text-sm text-slate-400">DentalPulse-Setup-{verified.version}.exe</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-dp-300" />
                </a>

                <a
                  data-testid="download-mac-intel"
                  href={verified.downloads.mac_intel}
                  className="flex items-center gap-4 p-5 rounded-xl bg-slate-800 border border-slate-700 hover:border-dp-500/50 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-slate-500/10 flex items-center justify-center">
                    <Apple className="w-6 h-6 text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white group-hover:text-dp-300 transition-colors">macOS (Intel)</p>
                    <p className="text-sm text-slate-400">DentalPulse-{verified.version}-x64.dmg</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-dp-300" />
                </a>

                <a
                  data-testid="download-mac-arm"
                  href={verified.downloads.mac_apple}
                  className="flex items-center gap-4 p-5 rounded-xl bg-slate-800 border border-slate-700 hover:border-dp-500/50 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg bg-slate-500/10 flex items-center justify-center">
                    <Apple className="w-6 h-6 text-slate-300" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white group-hover:text-dp-300 transition-colors">macOS (Apple Silicon)</p>
                    <p className="text-sm text-slate-400">DentalPulse-{verified.version}-arm64.dmg</p>
                  </div>
                  <Download className="w-5 h-5 text-slate-400 group-hover:text-dp-300" />
                </a>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <p className="text-xs text-slate-400 leading-relaxed">
                After installing, open DentalPulse and sign in with the credentials from your welcome email. The app will automatically check for updates and keep itself current.
              </p>
            </div>

            <Button
              data-testid="download-goto-login"
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full py-4 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-xl"
            >
              Go to Web Login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
